import React, { useState } from 'react';
import { FaTools, FaWrench } from 'react-icons/fa';
import ModelSelector from './ModelSelector';

interface ServiceListProps {
  selectedService: string;
  serviceType: 'installation' | 'reparation';
  onSubServiceSelect: (subService: string) => void;
}

// Définition des prix approximatifs par service et type
const servicePrices: Record<string, Record<string, string | number>> = {
  installation: {
    "Chauffe eau": "400€ à 1500€",
    "Douche": "Sur devis",
    "Baignoire": "Sur devis", 
    "Évier": "Sur devis",
    "Toilette": "À partir de 220€",
    "Lavabo": "À partir de 150€",
    "Sanibroyeur": "À partir de 300€",
    "Chauffage": "Sur devis",
    "Autre": "Sur devis"
  },
  reparation: {
    "Chauffe eau": "À partir de 120€",
    "Douche": "À partir de 90€",
    "Baignoire": "À partir de 90€",
    "Évier": "À partir de 80€",
    "Toilette": "À partir de 90€",
    "Lavabo": "À partir de 80€",
    "Sanibroyeur": "À partir de 110€",
    "Chauffage": "Sur devis",
    "Autre": "Sur devis"
  }
};

const serviceOptions: Record<string, Record<string, Array<{name: string, price: string, description: string}>>> = {
  "Chauffe eau": {
    installation: [
      {
        name: "Chauffe eau Vertical (15L à 300L)",
        price: "400€ à 1200€",
        description: "Fourniture de Chauffe eau de marque (Atlantic, Thermor, Ariston, Chaffoteaux, De Dietrich, Sauter)"
      },
      {
        name: "Chauffe eau Horizontal (45L à 200L)",
        price: "950€ à 1500€",
        description: "Fourniture de Chauffe eau de marque (Atlantic, Thermor, Ariston, Chaffoteaux, De Dietrich, Sauter)"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Pour toute autre demande (création sur mesure, installation de matériel fourni par vos soins, etc.)."
      }
    ],
    reparation: [
      {
        name: "Réparation d'une fuite",
        price: "À partir de 120€",
        description: "Réparation de fuite sur le chauffe eau"
      },
      {
        name: "Réparation d'une panne électrique",
        price: "À partir de 170€",
        description: "Diagnostic et réparation des pannes électriques"
      },
      {
        name: "Détartrage du chauffe eau",
        price: "À partir de 150€",
        description: "Détartrage complet pour optimiser les performances"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Toilette": {
    installation: [
      {
        name: "Installation d'un WC simpple standard",
        price: "À partir de 450€",
        description: "Fourniture et installation complète d'un WC simple"
      },
      {
        name: "Installation d'un WC simple de marque",
        price: "À partir de 550€",
        description: "Fourniture et installation complète d'un WC simple de marque Geberit, Grohe, Siamp, Allia etc..."
      },
      {
        name: "Installation d'un WC suspendu de marque",
        price: "À partir de 800€",
        description: "Fourniture et installation d'un WC suspendu avec bâti-support de marque Geberit, Grohe, Siamp, Allia etc..."
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Réparation d'une fuite",
        price: "À partir de 130€",
        description: "Réparation de fuite d'un WC"
      },
      {
        name: "Débouchage d'un WC",
        price: "À partir de 130€",
        description: "Débouchage complet d'un WC"
      },
      {
        name: "Recherche de fuite",
        price: "À partir de 120€",
        description: "Détection et localisation de fuites cachées"
      },
      {
        name: "Réparation d'une panne WC",
        price: "À partir de 130€",
        description: "Réparation spécialisée de WC simple"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Douche": {
    installation: [
      {
        name: "Installation d'un receveur de douche",
        price: "Sur devis",
        description: "Chaque projet de douche est unique. Diagnostic préalable nécessaire pour évaluer l'emplacement, le type de douche, les matériaux et contraintes techniques."
      },
      {
        name: "Installation d'un robinet de douche de marque",
        price: "À partir de 170€",
        description: "Fourniture d'un robinet de marque (Grohe, Hansgrohe, Jacob Delafon, Ideal Standard, etc.)."
      },
      {
        name: "Installation d'un robinet de douche standard",
        price: "À partir de 130€",
        description: "Fourniture et pose d'un robinet standard."
      },
      {
        name: "Réfection du joint de silicone",
        price: "À partir de 130€",
        description: "Fourniture et pose d'un joint de silicone au contour de la douche (blanc ou translucide)."
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Réparation d'un robinet de marque",
        price: "À partir de 130€",
        description: "Réparation spécialisée de robinet de marque"
      },
      {
        name: "Débouchage de la douche",
        price: "À partir de 130€",
        description: "Débouchage de l'évacuation de douche"
      },
      {
        name: "Réfection des joints de silicone",
        price: "À partir de 140€",
        description: "Réfection des joints d'étanchéité"
      },
      {
        name: "Recherche de fuite",
        price: "À partir de 120€",
        description: "Détection de fuites dans la douche"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Baignoire": {
    installation: [
      {
        name: "Installation d'un receveur de baignoire",
        price: "Sur devis",
        description: "Chaque projet de baignoire est unique. Diagnostic préalable nécessaire pour évaluer l'emplacement, le type de baignoire, les matériaux et contraintes techniques."
      },
      {
        name: "Installation d'un robinet de baignoire de marque",
        price: "À partir de 170€",
        description: "Fourniture d'un robinet de marque (Grohe, Hansgrohe, Jacob Delafon, Ideal Standard, etc.)."
      },
      {
        name: "Installation d'un robinet de baignoire standard",
        price: "À partir de 140€",
        description: "Fourniture et pose d'un robinet standard."
      },
      {
        name: "Réfection du joint de silicone",
        price: "À partir de 140€",
        description: "Fourniture et pose d'un joint de silicone au contour de la baignoire blanc ou translucide."
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Réparation d'un robinet de marque",
        price: "À partir de 130€",
        description: "Réparation spécialisée de robinet de marque"
      },
      {
        name: "Débouchage d'une baignoire",
        price: "À partir de 130€",
        description: "Débouchage de l'évacuation de baignoire"
      },
      {
        name: "Réfection des joints de silicone",
        price: "À partir de 140€",
        description: "Réfection des joints d'étanchéité"
      },
      {
        name: "Recherche de fuite",
        price: "À partir de 120€",
        description: "Détection de fuites dans la baignoire"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Évier": {
    installation: [
      {
        name: "Installation d'un évier",
        price: "Sur devis",
        description: "Installation personnalisée d'évier selon vos besoins et contraintes"
      },
      {
        name: "Installation d'un robinet d'un évier de marque",
        price: "À partir de 170€",
        description: "Fourniture d'un robinet de marque (Grohe, Hansgrohe, Jacob Delafon, Ideal Standard, etc.)."
      },
      {
        name: "Installation d'un robinet d'un évier standard",
        price: "À partir de 140€",
        description: "Fourniture et pose d'un robinet standard."
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Réparation d'un robinet de marque",
        price: "À partir de 130€",
        description: "Réparation spécialisée de robinet de marque"
      },
      {
        name: "Débouchage de l'évier",
        price: "À partir de 130€",
        description: "Débouchage de l'évacuation d'évier"
      },
      {
        name: "Recherche de fuite",
        price: "À partir de 120€",
        description: "Détection de fuites sous l'évier"
      },
      {
        name: "Réparation d'une fuite",
        price: "À partir de 130€",
        description: "Réparation de fuite sous l'évier"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Lavabo": {
    installation: [
      {
        name: "Installation d'un lavabo de marque",
        price: "À partir de 300€",
        description: "Fourniture et installation d'un lavabo de marque Jacob Delafon, Ideal Standard, Allia, Roca, etc..."
      },
      {
        name: "Installation d'un robinet de lavabo standard",
        price: "À partir de 140€",
        description: "Fourniture et pose d'un robinet standard."
      },
      {
        name: "Installation d'un robinet de lavabo de marque",
        price: "À partir de 170€",
        description: "Fourniture d'un robinet de marque (Grohe, Hansgrohe, Jacob Delafon, Ideal Standard, etc.)."
      },
      {
        name: "Installation d'un meuble lavabo",
        price: "Sur devis",
        description: "Fourniture et installation d'un meuble lavabo"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Réparation d'un robinet de marque",
        price: "À partir de 130€",
        description: "Réparation spécialisée de robinet de marque"
      },
      {
        name: "Débouchage d'un lavabo",
        price: "À partir de 120€",
        description: "Débouchage complet d'un lavabo"
      },
      {
        name: "Recherche de fuite",
        price: "À partir de 90€",
        description: "Détection de fuites sur un lavabo"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Sanibroyeur": {
    installation: [
      {
        name: "Installation d'un sanibroyeur de marque",
        price: "À partir de 550€",
        description: "Fourniture et installation du sanibroyeur de marque SFA."
      },
      {
        name: "Installation d'un WC avec moteur intégré de marque",
        price: "À partir de 950€",
        description: "Fourniture et installation de WC avec moteur intégré de marque SFA."
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Débouchage du sanibroyeur",
        price: "À partir de 180€",
        description: "Débouchage et maintenance du sanibroyeur"
      },
      {
        name: "Réparation moteur",
        price: "Sur devis",
        description: "Réparation du moteur."
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Chauffage": {
    installation: [
      {
        name: "Installation d'une chaudière à gaz",
        price: "Sur devis",
        description: "Fourniture et installation d'une chaudière à gaz"
      },
      {
        name: "Installation d'un radiateur",
        price: "Sur devis",
        description: "Fourniture et installation de radiateurs"
      },
      {
        name: "Installation d'un robinet de radiateur",
        price: "À partir de 170€",
        description: "Fourniture et installation d'un robinet de radiateur"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ],
    reparation: [
      {
        name: "Réparation d'une fuite",
        price: "À partir de 150€",
        description: "Réparation de fuite sur le circuit de chauffage"
      },
      {
        name: "Purge des radiateurs",
        price: "À partir de 80€",
        description: "Purge complète des radiateurs"
      },
      {
        name: "Désembouage",
        price: "À partir de 200€",
        description: "Nettoyage complet du circuit de chauffage."
      },
      {
        name: "Réparation d'une panne",
        price: "Sur devis",
        description: "Diagnostic et réparation de panne de chauffage"
      },
      {
        name: "Autre",
        price: "Sur devis",
        description: "Vous pourrez décrire votre problème dans votre demande."
      }
    ]
  },

  "Autre": {
    installation: [
      {
        name: "Installation sur mesure",
        price: "Sur devis",
        description: "Installation personnalisée selon vos besoins"
      }
    ],
    reparation: [
      {
        name: "Intervention d'urgence",
        price: "Sur devis",
        description: "Intervention d'urgence 24h/24"
      }
    ]
  }
};

const ServiceList: React.FC<ServiceListProps> = ({ selectedService, serviceType, onSubServiceSelect }) => {
  const [selectedSubService, setSelectedSubService] = useState<string | null>(null);
  const [modelSelection, setModelSelection] = useState<any>(null);

  const handleSubServiceClick = (subService: string) => {
    setSelectedSubService(subService);
    onSubServiceSelect(subService);
  };

  const handleModelSelectionChange = (selection: any) => {
    setModelSelection(selection);
  };

  if (!selectedService || !serviceOptions[selectedService]) {
    return null;
  }

  const options = serviceOptions[selectedService][serviceType] || [];

  return (
    <div className="mt-6 p-6 bg-gray-50 rounded-2xl border border-gray-200 animate-fadeIn">
      <h3 className="text-lg font-semibold text-secondary mb-4">
        Prestations disponibles pour : <span className="text-primary">{selectedService}</span>
      </h3>
      
      <div className="grid gap-3">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleSubServiceClick(option.name)}
            className={`p-4 rounded-lg border transition-all duration-300 text-left ${
              selectedSubService === option.name
                ? 'bg-primary/10 border-primary shadow-md'
                : 'bg-white border-gray-200 hover:border-primary/30 hover:shadow-sm'
            }`}
          >
            <div>
              <div className="flex justify-between items-baseline">
                <h4 className={`font-medium ${selectedSubService === option.name ? 'text-primary' : 'text-gray-800'} pr-4`}>
                  {option.name}
                </h4>
                <span className={`font-bold whitespace-nowrap ${selectedSubService === option.name ? 'text-primary' : 'text-gray-700'}`}>
                  {option.price}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{option.description}</p>
            </div>
          </button>
        ))}
      </div>

      {selectedSubService && (
        <>
          <ModelSelector 
            serviceType={selectedService}
            selectedOption={selectedSubService}
            onSelectionChange={handleModelSelectionChange}
          />
          
          <div className="mt-4 p-4 bg-white rounded-lg border border-primary/20">
            <p className="text-sm text-gray-600">
              Prestation sélectionnée : <span className="font-medium text-primary">{selectedSubService}</span>
            </p>
            {modelSelection && Object.keys(modelSelection).length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                <p>Choix personnalisés :</p>
                <ul className="list-disc list-inside ml-2">
                  {modelSelection.capacity && <li>Capacité : {modelSelection.capacity}</li>}
                  {modelSelection.brand && <li>Marque : {modelSelection.brand}</li>}
                  {modelSelection.type && <li>Type : {modelSelection.type}</li>}
                  {modelSelection.material && <li>Matériau : {modelSelection.material}</li>}
                  {modelSelection.additionalInfo && <li>Infos : {modelSelection.additionalInfo}</li>}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Cliquez sur "Contacter un plombier" pour obtenir un devis personnalisé.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceList; 